// frontend/src/components/common/ModalFechaContador.jsx
// Modal de fecha + contador (opcional) — reemplaza los window.prompt() encadenados
// que pedían estos dos datos por separado en entrega/devolución/aviso de fin.
// Uso: const datos = await pedirFechaContador({ titulo, descripcion, fechaInicial, labelContador, pedirTransporte })
// Retorna { fecha, contador, transporte } o null si el usuario canceló.
// `transporte` viaja siempre como objeto (posiblemente con todos los campos
// vacíos) cuando `pedirTransporte` es true, para que el llamador lo mande tal
// cual al backend sin tener que armar la forma del payload.
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import SimpleSelectField from './SimpleSelectField.jsx';

// ─── Context ──────────────────────────────────────────────────────────────────

const FechaContadorContext = createContext(null);

const OPCIONES_DEFAULT = {
  titulo:            'Registrar fecha',
  descripcion:       '',
  labelFecha:        'Fecha',
  fechaInicial:      '',
  pedirContador:     true,
  contadorRequerido: false,
  labelContador:     'Contador (opcional)',
  labelConfirmar:    'Confirmar',
  labelCancelar:     'Cancelar',
  // Sección de transporte y responsables (remito de entrega/devolución de
  // alquiler). Todo opcional: un contrato puede confirmarse sin cargar nada
  // de esto, el remito sale con esos campos en blanco.
  pedirTransporte:      false,
  labelEntrego:         'Entregó',
  labelRecibio:         'Recibió',
  vehiculosPropios:     [],  // catálogo de FlotaTransporte, provisto por el llamador
  transportistas:       [],  // catálogo de Transportista, provisto por el llamador
};

const TRANSPORTE_DEFAULT = {
  transporte_modalidad: '',
  transporte_vehiculo: '',
  transportista: '',
  patente: '',
  chofer: '',
  entrego: '',
  recibio: '',
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const FechaContadorProvider = ({ children }) => {
  const [estado, setEstado] = useState({ visible: false, opciones: OPCIONES_DEFAULT });
  const [fecha, setFecha] = useState('');
  const [contador, setContador] = useState('');
  const [transporte, setTransporte] = useState(TRANSPORTE_DEFAULT);
  const [errores, setErrores] = useState({});
  const resolveRef = useRef(null);
  const inputRef = useRef(null);

  const pedirFechaContador = useCallback((opciones = {}) => {
    return new Promise((resolve) => {
      const merged = { ...OPCIONES_DEFAULT, ...opciones };
      setEstado({ visible: true, opciones: merged });
      setFecha(merged.fechaInicial || '');
      setContador('');
      setTransporte(TRANSPORTE_DEFAULT);
      setErrores({});
      resolveRef.current = resolve;
    });
  }, []);

  const cambiarTransporte = useCallback((campo, valor) => {
    setTransporte((t) => {
      // Cambiar de modalidad limpia el otro lado (propio/tercero): un vehículo
      // propio elegido no debe sobrevivir como dato fantasma si el usuario
      // pasa a "tercero", y viceversa.
      if (campo === 'transporte_modalidad') {
        return { ...t, transporte_modalidad: valor, transporte_vehiculo: '', transportista: '' };
      }
      return { ...t, [campo]: valor };
    });
  }, []);

  useEffect(() => {
    if (estado.visible) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [estado.visible]);

  const resolver = useCallback((resultado) => {
    setEstado(s => ({ ...s, visible: false }));
    setErrores({});
    setTimeout(() => resolveRef.current?.(resultado), 120);
  }, []);

  const handleAceptar = useCallback(() => {
    if (!fecha) {
      setErrores({ fecha: 'Indicá la fecha.' });
      return;
    }
    if (estado.opciones.contadorRequerido && !contador.trim()) {
      setErrores({ contador: 'Este equipo tiene contador: indicá la lectura.' });
      return;
    }
    resolver({
      fecha,
      contador: contador.trim() || null,
      transporte: estado.opciones.pedirTransporte ? transporte : null,
    });
  }, [fecha, contador, transporte, estado.opciones.contadorRequerido, estado.opciones.pedirTransporte, resolver]);

  const handleCancelar = useCallback(() => {
    resolver(null);
  }, [resolver]);

  useEffect(() => {
    if (!estado.visible) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') handleCancelar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [estado.visible, handleCancelar]);

  return (
    <FechaContadorContext.Provider value={{ pedirFechaContador }}>
      {children}
      {estado.visible && (
        <FechaContadorUI
          opciones={estado.opciones}
          fecha={fecha}
          onChangeFecha={setFecha}
          contador={contador}
          onChangeContador={setContador}
          transporte={transporte}
          onChangeTransporte={cambiarTransporte}
          errores={errores}
          inputRef={inputRef}
          onAceptar={handleAceptar}
          onCancelar={handleCancelar}
        />
      )}
    </FechaContadorContext.Provider>
  );
};

export const useFechaContador = () => {
  const ctx = useContext(FechaContadorContext);
  if (!ctx) throw new Error('useFechaContador debe usarse dentro de FechaContadorProvider');
  return ctx;
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(-8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

// ─── Styled ───────────────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${({ theme }) => theme.colors.overlayBackdrop};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  animation: ${fadeIn} 0.15s ease both;
  padding: 16px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.colors.shadowModal};
  width: 100%;
  max-width: ${({ $ancho }) => ($ancho === 'lg' ? '560px' : '420px')};
  max-height: 90vh;
  overflow-y: auto;
  animation: ${scaleIn} 0.18s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const TopBar = styled.div`
  height: 3px;
  background: ${({ theme }) => theme.colors.primary};
`;

const Body = styled.div`
  padding: 22px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Titulo = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const Descripcion = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const CampoWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InputField = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text};
  /* bgPrincipal, un nivel mas oscuro que el bgSecundario del Folio que
     contiene al input (criterio: inputs mas oscuros que su entorno). */
  background: ${({ theme }) => theme.colors.bgPrincipal};
  border: 1px solid ${({ $error, theme }) => $error ? theme.colors.danger : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-sizing: border-box;
  transition: border-color 0.15s;

  /* Placeholder explícito: sin esto el navegador aplica su gris por defecto,
     que en el tema oscuro queda casi ilegible sobre el fondo del propio
     input, desintegrado del resto de los formularios. */
  &::placeholder {
    color: ${({ theme }) => theme.colors.txtMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.shadowFocus};
  }
`;

const ErrorMsg = styled.span`
  display: block;
  margin-top: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.danger};
`;

// ─── Folio ────────────────────────────────────────────────────────────────────
// Mismo patrón que AlmacenPage.jsx ("Folio + sello de tono semántico"): agrupa
// visualmente cada bloque del remito con un color que dice algo, en vez de una
// grilla plana toda celeste. Acá el bloque de fecha/contador es la sección
// operativa obligatoria (tono `info`, igual criterio que "Repuestos" en
// Almacén) y el bloque de transporte es un traslado físico (tono `naranja`,
// igual criterio que "transferencia" en Almacén) — dos folios, dos colores,
// en vez de un único celeste repetido en cada control del modal.
const TONO_TOKENS = {
  info: ['infoBg', 'infoTxt', 'infoBorder'],
  naranja: ['naranjaBg', 'naranjaTxt', 'naranjaBorder'],
};

const tonoTokens = (theme, tono) => {
  const keys = TONO_TOKENS[tono];
  if (!keys) return null;
  const [bgKey, txtKey, borderKey] = keys;
  return { bg: theme.colors[bgKey], txt: theme.colors[txtKey], border: theme.colors[borderKey] };
};

const FolioStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Folio = styled.div`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-left: 3px solid ${({ theme, $tono }) => tonoTokens(theme, $tono)?.border || theme.colors.borderDefault};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FolioHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const FolioIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  flex-shrink: 0;
  font-size: 12px;
  background: ${({ theme, $tono }) => tonoTokens(theme, $tono)?.bg || theme.colors.surface};
  color: ${({ theme, $tono }) => tonoTokens(theme, $tono)?.txt || theme.colors.txtSecondary};
`;

const FolioTitle = styled.h4`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

const Fila = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || '1fr 1fr'};
  gap: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 24px 20px;
`;

const BtnBase = styled.button`
  padding: 8px 18px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
  min-width: 90px;
  border: 1px solid transparent;
`;

const BtnCancelar = styled(BtnBase)`
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-color: ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const BtnAceptar = styled(BtnBase)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.onPrimary};

  &:hover { opacity: 0.88; }
  &:active { opacity: 0.75; }
`;

// ─── UI ───────────────────────────────────────────────────────────────────────

const MODALIDAD_OPTIONS = [
  { value: '', label: 'Sin especificar' },
  { value: 'propio', label: 'Vehículo propio' },
  { value: 'tercero', label: 'Transportista tercero' },
];

const FechaContadorUI = ({
  opciones, fecha, onChangeFecha, contador, onChangeContador,
  transporte, onChangeTransporte, errores, inputRef, onAceptar, onCancelar,
}) => {
  const {
    titulo, descripcion, labelFecha, pedirContador, contadorRequerido,
    labelContador, labelConfirmar, labelCancelar, pedirTransporte,
    labelEntrego, labelRecibio, vehiculosPropios, transportistas,
  } = opciones;

  return (
    <Backdrop onClick={e => e.target === e.currentTarget && onCancelar()}>
      <Card role="dialog" aria-modal="true" $ancho={pedirTransporte ? 'lg' : 'sm'}>
        <TopBar />
        <Body>
          <Titulo>{titulo}</Titulo>
          {descripcion && <Descripcion>{descripcion}</Descripcion>}
          <FolioStack>
            <Folio $tono="info">
              <FolioHeader>
                <FolioIcon $tono="info"><i className="bi bi-calendar-check" /></FolioIcon>
                <FolioTitle>{pedirTransporte ? 'Entrega / Devolución' : labelFecha}</FolioTitle>
              </FolioHeader>
              <Fila>
                <CampoWrap>
                  <Label>{labelFecha}</Label>
                  <InputField
                    ref={inputRef}
                    type="date"
                    value={fecha}
                    onChange={e => onChangeFecha(e.target.value)}
                    $error={!!errores.fecha}
                  />
                  {errores.fecha && <ErrorMsg>{errores.fecha}</ErrorMsg>}
                </CampoWrap>
                {pedirContador && (
                  <CampoWrap>
                    <Label>{labelContador}{contadorRequerido ? ' *' : ''}</Label>
                    <InputField
                      type="number"
                      step="0.01"
                      min="0"
                      value={contador}
                      onChange={e => onChangeContador(e.target.value)}
                      placeholder={contadorRequerido ? '' : 'Dejar en blanco si no corresponde'}
                      $error={!!errores.contador}
                    />
                    {errores.contador && <ErrorMsg>{errores.contador}</ErrorMsg>}
                  </CampoWrap>
                )}
              </Fila>
            </Folio>

            {pedirTransporte && (
              <Folio $tono="naranja">
                <FolioHeader>
                  <FolioIcon $tono="naranja"><i className="bi bi-truck" /></FolioIcon>
                  <FolioTitle>Transporte</FolioTitle>
                </FolioHeader>
                <Fila>
                  <CampoWrap>
                    <Label>Modalidad</Label>
                    <SimpleSelectField
                      options={MODALIDAD_OPTIONS}
                      value={transporte.transporte_modalidad}
                      onChange={(v) => onChangeTransporte('transporte_modalidad', v)}
                      getOptionValue={(o) => o.value}
                      getOptionLabel={(o) => o.label}
                      placeholder="Sin especificar"
                    />
                  </CampoWrap>
                  {transporte.transporte_modalidad === 'propio' && (
                    <CampoWrap>
                      <Label>Vehículo propio</Label>
                      <SimpleSelectField
                        options={vehiculosPropios}
                        value={transporte.transporte_vehiculo}
                        onChange={(v) => onChangeTransporte('transporte_vehiculo', v)}
                        getOptionValue={(o) => String(o.equipo)}
                        getOptionLabel={(o) => o.equipo_interno}
                        placeholder="Elegir unidad"
                      />
                    </CampoWrap>
                  )}
                  {transporte.transporte_modalidad === 'tercero' && (
                    <CampoWrap>
                      <Label>Transportista</Label>
                      <SimpleSelectField
                        options={transportistas}
                        value={transporte.transportista}
                        onChange={(v) => onChangeTransporte('transportista', v)}
                        getOptionValue={(o) => String(o.id)}
                        getOptionLabel={(o) => o.nombre}
                        placeholder="Elegir transportista"
                      />
                    </CampoWrap>
                  )}
                </Fila>
                <Fila>
                  <CampoWrap>
                    <Label>Patente</Label>
                    <InputField
                      value={transporte.patente}
                      onChange={e => onChangeTransporte('patente', e.target.value)}
                      placeholder="Opcional"
                    />
                  </CampoWrap>
                  <CampoWrap>
                    <Label>Chofer</Label>
                    <InputField
                      value={transporte.chofer}
                      onChange={e => onChangeTransporte('chofer', e.target.value)}
                      placeholder="Opcional"
                    />
                  </CampoWrap>
                </Fila>
                <Fila>
                  <CampoWrap>
                    <Label>{labelEntrego}</Label>
                    <InputField
                      value={transporte.entrego}
                      onChange={e => onChangeTransporte('entrego', e.target.value)}
                      placeholder="Opcional"
                    />
                  </CampoWrap>
                  <CampoWrap>
                    <Label>{labelRecibio}</Label>
                    <InputField
                      value={transporte.recibio}
                      onChange={e => onChangeTransporte('recibio', e.target.value)}
                      placeholder="Opcional"
                    />
                  </CampoWrap>
                </Fila>
              </Folio>
            )}
          </FolioStack>
        </Body>
        <Footer>
          <BtnCancelar onClick={onCancelar}>{labelCancelar}</BtnCancelar>
          <BtnAceptar onClick={onAceptar}>{labelConfirmar}</BtnAceptar>
        </Footer>
      </Card>
    </Backdrop>
  );
};
